import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { syncUser } from '@/api/api'
import { getAccessToken, signOut } from '@/lib/auth'
import { updateAuthToken } from '@/lib/auth-service-worker'
import { useAppStore, useAppStorePersisted } from '@/store/useAppStore'
import { useAuthUserId, useIsSignedIn } from '@/store/useAuthStore'
import { useShortcutStore } from '@/store/useShortcutStore'

/**
 * サインイン後、バックエンドとユーザー情報を同期するフック
 * サインイン時に一度だけ /users/me を呼び出してユーザー情報を同期
 * Service Workerにもトークンを送信
 * @returns isReady - 認証準備完了フラグ（トークン取得可能になるまでfalse）
 */
export const useAuthSync = () => {
  const isSignedIn = useIsSignedIn()
  const authUserId = useAuthUserId()
  const hasSynced = useRef(false)
  const [isReady, setIsReady] = useState(false)
  const setAccessiblePages = useAppStore((state) => state.setAccessiblePages)
  const setAccessibleDomains = useAppStore((state) => state.setAccessibleDomains)
  const setHasFullAccess = useAppStore((state) => state.setHasFullAccess)
  const setIsSafeMode = useAppStore((state) => state.setIsSafeMode)
  const setFeatures = useAppStore((state) => state.setFeatures)
  const setCurrentUserId = useAppStore((state) => state.setCurrentUserId)
  const setAuthSyncStatus = useAppStore((state) => state.setAuthSyncStatus)
  const cacheAuthData = useAppStorePersisted((state) => state.cacheAuthData)

  // トークンが取れる状態になったら認証準備完了とする
  useEffect(() => {
    if (!isSignedIn) return
    getAccessToken()
      .then((token) => {
        if (token) {
          setIsReady(true)
        }
      })
      .catch((error) => {
        console.error('[useAuthSync] Failed to get initial token:', error)
      })
  }, [isSignedIn])

  // Service Workerにトークンを送り直す（SW は停止・再起動でメモリ上のトークンを失うため）
  useEffect(() => {
    if (!isSignedIn) {
      updateAuthToken(null)
      return
    }

    const sendTokenToSW = async () => {
      if (document.visibilityState === 'hidden') return
      try {
        updateAuthToken(await getAccessToken())
      } catch (error) {
        console.error('Failed to update SW token:', error)
      }
    }

    sendTokenToSW()

    // 期限が近いトークンは getAccessToken 内で更新されるので、ネットワークリクエストはその時だけ発生する
    const interval = setInterval(sendTokenToSW, 5 * 60 * 1000)

    // タブが可視状態に戻った時にも更新（長時間バックグラウンド後の復帰対策）
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendTokenToSW()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isSignedIn])

  useEffect(() => {
    const sync = async () => {
      if (!isSignedIn || !authUserId || hasSynced.current) return

      setAuthSyncStatus('loading')
      try {
        const response = await syncUser()
        // 防御的コーディング: キャッシュから空のレスポンスが返る可能性がある
        if (!response || !response.pages) {
          console.warn('[useAuthSync] Invalid response from syncUser:', response)
          setAuthSyncStatus('error')
          return
        }
        const domains = response.content_access?.domains ?? []
        const hasFullAccess = response.content_access?.has_full_access ?? false
        const isSafeMode = response.content_access?.safe_mode ?? false
        const features = response.features ?? []
        const userId = response.id ?? null
        setAccessiblePages(response.pages)
        setAccessibleDomains(domains)
        setHasFullAccess(hasFullAccess)
        setIsSafeMode(isSafeMode)
        setFeatures(features)
        setCurrentUserId(userId)
        setAuthSyncStatus('success')
        // オフライン用にキャッシュ
        cacheAuthData({ pages: response.pages, domains, hasFullAccess, isSafeMode, features, userId })
        hasSynced.current = true
        // ショートカットを取得
        useShortcutStore.getState().fetchShortcuts()
      } catch (error) {
        console.error('Failed to sync user with backend:', error)
        // 未登録（削除済みを含む）のアカウントは再試行しても通らないので、サインアウトして理由を伝える
        if (axios.isAxiosError<{ detail?: unknown }>(error) && error.response?.status === 403) {
          const detail = error.response.data?.detail
          toast.error(typeof detail === 'string' ? detail : 'このアカウントは登録されていません')
          await signOut().catch((signOutError: unknown) => console.error('Failed to sign out:', signOutError))
          return
        }
        setAuthSyncStatus('error')
      }
    }

    sync()
  }, [
    isSignedIn,
    authUserId,
    setAccessiblePages,
    setAccessibleDomains,
    setHasFullAccess,
    setIsSafeMode,
    setFeatures,
    setCurrentUserId,
    setAuthSyncStatus,
    cacheAuthData,
  ])

  // サインアウト時にリセット
  useEffect(() => {
    if (!isSignedIn) {
      hasSynced.current = false
      setIsReady(false)
      setAuthSyncStatus('idle')
    }
  }, [isSignedIn, setAuthSyncStatus])

  return { isReady }
}
